/**
 * Tastenführung für eine Vorschlagsliste unter einem Eingabefeld.
 *
 * Der Fokus bleibt im Feld - man tippt weiter, auch wenn gerade ein Vorschlag
 * markiert ist. `aria-activedescendant` sagt dem Bildschirmleser, welcher.
 */
import { vorschlaege, ortsname, ortsdetail } from '../data/suche.js';

let laufendeNummer = 0;

export function listenTasten(input, liste, { onEnter, onEscape } = {}) {
  const knoepfe = () => [...liste.querySelectorAll('button')];
  const aktiver = () => liste.querySelector('button.is-aktiv');

  function markieren(b) {
    for (const k of knoepfe()) {
      k.classList.toggle('is-aktiv', k === b);
      k.setAttribute('aria-selected', String(k === b));
    }
    if (b) {
      if (!b.id) { laufendeNummer += 1; b.id = `vorschlag-${laufendeNummer}`; }
      input.setAttribute('aria-activedescendant', b.id);
      b.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  input.addEventListener('keydown', (e) => {
    const offen = !liste.hidden;
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && offen) {
      e.preventDefault();
      const alle = knoepfe();
      if (!alle.length) return;
      const i = alle.indexOf(aktiver());
      const schritt = e.key === 'ArrowDown' ? 1 : -1;
      markieren(alle[(i + schritt + alle.length) % alle.length] ?? alle[0]);
    } else if (e.key === 'Enter') {
      const a = offen ? aktiver() : null;
      e.preventDefault();
      if (a) a.click();
      else onEnter?.();
    } else if (e.key === 'Escape') {
      // Ist eine Liste offen, schliesst Esc nur sie - nicht auch die Spalte dahinter.
      if (offen) e.stopPropagation();
      onEscape?.();
    }
  });

  // Ohne das nimmt der Klick dem Feld den Fokus, bevor er ankommt - und das
  // `change` des Felds rechnet dann mit dem halb getippten Text.
  liste.addEventListener('mousedown', (e) => e.preventDefault());

  return { markieren };
}

/**
 * Ortsfeld der Routenplanung: Vorschläge beim Tippen.
 * `onWahl` bekommt { lat, lng, name }.
 */
export function ortsfeld({ input, map, onWahl }) {
  const liste = document.createElement('ul');
  liste.className = 'search__results ortsfeld__liste';
  liste.setAttribute('role', 'listbox');
  liste.hidden = true;
  input.parentElement.append(liste);

  let timer;
  let controller;

  const zu = () => {
    clearTimeout(timer);
    controller?.abort();
    liste.hidden = true;
    liste.textContent = '';
  };

  function zeigen(treffer) {
    liste.textContent = '';
    if (!treffer.length) {
      const li = document.createElement('li');
      li.className = 'is-empty';
      li.textContent = 'Nichts gefunden.';
      liste.append(li);
    }
    for (const t of treffer) {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.tabIndex = -1;
      b.setAttribute('role', 'option');
      const kopf = ortsname(t);
      b.innerHTML = '<strong></strong><small></small>';
      b.querySelector('strong').textContent = kopf;
      b.querySelector('small').textContent = ortsdetail(t, kopf);
      b.addEventListener('click', () => {
        zu();
        onWahl({ lat: Number(t.lat), lng: Number(t.lon), name: ortsname(t, { mitOrt: true }) });
      });
      li.append(b);
      liste.append(li);
    }
    liste.hidden = false;
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearTimeout(timer);
    controller?.abort();
    if (q.length < 3) { liste.hidden = true; return; }
    timer = setTimeout(async () => {
      controller = new AbortController();
      try {
        zeigen(await vorschlaege(q, { map, signal: controller.signal, limit: 6 }));
      } catch (e) {
        if (e.name !== 'AbortError') liste.hidden = true;
      }
    }, 250);
  });

  input.addEventListener('blur', () => setTimeout(zu, 120));
  listenTasten(input, liste, { onEscape: zu, onEnter: () => input.blur() });
  return { zu };
}
